import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@shared/src/utils/cn";

// Card Root Variants
const cardVariants = cva("border text-card-foreground shadow", {
  variants: {
    variant: {
      default: "bg-cards-card-bg border-cards-card-border",
      primary: "bg-surfaces-bg-primary border-border-secondary",
      secondary: "bg-surfaces-bg-secondary border-border-secondary",
      tertiary: "bg-surfaces-bg-tertiary border-border-tertiary",
      elevated: "bg-cards-card-bg border-cards-card-border shadow-lg",
      outlined: "bg-transparent border-border-default shadow-none",
      ghost: "bg-transparent border-transparent shadow-none",
      // New semantic variants for theme-aware overlays
      "overlay-subtle": "bg-surfaces-bg-primary/5 border-border-secondary/20 backdrop-blur-sm",
      "overlay-moderate": "bg-surfaces-bg-primary/10 border-border-secondary/30",
      "overlay-strong": "bg-surfaces-bg-primary/20 border-border-secondary/40",
      // White overlay variants for the existing use cases
      "white-overlay": "bg-white-100/5 border border-border-tertiary",
      "white-overlay-moderate": "bg-white-100/10 border border-border-tertiary",
      "white-overlay-strong": "bg-white-100/20 border border-border-tertiary",
      // Special case for contexts requiring pure backgrounds
      "contrast-high": "bg-white-100 border-border-secondary shadow-sm dark:bg-neutral-100",
      "contrast-paper": "bg-white-100 border-border-default dark:bg-neutral-50",
    },
    size: {
      default: "p-6",
      sm: "p-4",
      md: "p-6",
      lg: "p-8",
      none: "p-0", // for custom padding
    },
    radius: {
      default: "rounded-xl",
      sm: "rounded-lg",
      md: "rounded-xl",
      lg: "rounded-2xl",
      xl: "rounded-3xl",
      none: "rounded-none",
    },
    hover: {
      none: "",
      subtle: "hover:bg-surfaces-bg-hover transition-colors",
      moderate: "hover:bg-surfaces-bg-hover/80 transition-colors",
      overlay: "hover:bg-surfaces-bg-primary/15 transition-all duration-200",
      "white-overlay": "hover:bg-white-100/10 transition-colors",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
    radius: "default",
    hover: "none",
  },
});

// Card Header Variants
const cardHeaderVariants = cva("flex flex-col", {
  variants: {
    spacing: {
      default: "space-y-1.5 p-6",
      sm: "space-y-1 p-4",
      md: "space-y-1.5 p-6",
      lg: "space-y-2 p-8",
      none: "space-y-1.5", // no padding
    },
  },
  defaultVariants: {
    spacing: "default",
  },
});

// Card Content Variants
const cardContentVariants = cva("", {
  variants: {
    spacing: {
      default: "p-6 pt-0",
      sm: "p-4 pt-0",
      md: "p-6 pt-0",
      lg: "p-8 pt-0",
      none: "pt-0", // no padding
    },
  },
  defaultVariants: {
    spacing: "default",
  },
});

// Card Footer Variants
const cardFooterVariants = cva("flex items-center", {
  variants: {
    spacing: {
      default: "p-6 pt-0",
      sm: "p-4 pt-0",
      md: "p-6 pt-0",
      lg: "p-8 pt-0",
      none: "pt-0", // no padding
    },
  },
  defaultVariants: {
    spacing: "default",
  },
});

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardVariants>>(
  ({ className, variant, size, radius, hover, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant, size, radius, hover }), className)} {...props} />
  ),
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardHeaderVariants>
>(({ className, spacing, ...props }, ref) => (
  <div ref={ref} className={cn(cardHeaderVariants({ spacing }), className)} {...props} />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("font-semibold leading-none tracking-tight", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("text-sm text-text-secondary", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardContentVariants>
>(({ className, spacing, ...props }, ref) => (
  <div ref={ref} className={cn(cardContentVariants({ spacing }), className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardFooterVariants>
>(({ className, spacing, ...props }, ref) => (
  <div ref={ref} className={cn(cardFooterVariants({ spacing }), className)} {...props} />
));
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
